const TARGET_ORDER = ['details', 'benefits', 'reviews', 'ingredients', 'faqs'];
const REVEAL_SCROLL_Y = 200;

const HEADING_PATTERNS = {
  details: /\b(product\s*)?(details?|description)\b/i,
  faqs: /\b(faqs?|frequently asked questions)\b/i,
};

class ProductInformationNavigation extends HTMLElement {
  targets = new Map();
  links = new Map();
  activeKey = '';
  frame = 0;
  mutationTimer = 0;
  resizeObserver = null;
  mutationObserver = null;

  connectedCallback() {
    if (this.isConnectedAndReady) return;
    this.isConnectedAndReady = true;

    this.parentSection = this.closest('[data-product-information-details]');
    this.targetScope = this.parentSection || document;
    this.scroller = this.querySelector('.product-information-navigation__scroller');
    this.querySelectorAll('[data-product-info-link]').forEach((link) => {
      const key = link.dataset.productInfoLink;
      if (key) this.links.set(key, link);
    });
    this.updateRevealState();

    this.addEventListener('click', this.handleClick);
    window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });

    this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
    this.resizeObserver.observe(document.documentElement);
    this.resizeObserver.observe(this);
    const header = document.querySelector('#header-component, header-component');
    if (header) this.resizeObserver.observe(header);

    this.mutationObserver = new MutationObserver(this.handleMutations);
    this.mutationObserver.observe(this.targetScope, { childList: true, subtree: true });

    this.refreshTargets();
  }

  disconnectedCallback() {
    this.isConnectedAndReady = false;
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.scheduleUpdate);
    window.removeEventListener('resize', this.handleResize);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    window.cancelAnimationFrame(this.frame);
    window.clearTimeout(this.mutationTimer);
  }

  handleMutations = (mutations) => {
    const mayContainTarget = mutations.some((mutation) =>
      [...mutation.addedNodes, ...mutation.removedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches?.('.shopify-section, details, [data-product-info-target], .jdgm-widget, #judgeme_product_reviews') ||
            node.querySelector?.('details, [data-product-info-target], .jdgm-widget, #judgeme_product_reviews'))
      )
    );

    if (!mayContainTarget) return;
    window.clearTimeout(this.mutationTimer);
    this.mutationTimer = window.setTimeout(() => this.refreshTargets(), 80);
  };

  refreshTargets() {
    const nextTargets = new Map();

    for (const key of TARGET_ORDER) {
      const target = this.findTarget(key);
      const link = this.links.get(key);
      if (!link) continue;

      link.hidden = !target;
      link.toggleAttribute('disabled', !target);
      if (target) {
        if (!target.id) target.id = `product-info-${key}`;
        link.setAttribute('aria-controls', target.id);
        nextTargets.set(key, target);
      } else {
        link.removeAttribute('aria-controls');
        link.removeAttribute('aria-current');
      }
    }

    this.targets = nextTargets;
    this.scheduleUpdate();
  }

  findTarget(key) {
    if (key === 'benefits') {
      const dedicatedBenefits = document.querySelector(
        '[data-benefits-accordion][data-product-info-target="benefits"]'
      );
      return dedicatedBenefits && !this.contains(dedicatedBenefits) ? dedicatedBenefits : null;
    }

    if (key === 'ingredients') {
      const dedicatedIngredients = document.querySelector(
        '[data-key-ingredients][data-product-info-target="ingredients"]'
      );
      return dedicatedIngredients && !this.contains(dedicatedIngredients) ? dedicatedIngredients : null;
    }

    const semanticTarget = this.targetScope.querySelector(`[data-product-info-target="${key}"]`);
    if (semanticTarget && !this.contains(semanticTarget)) return semanticTarget;

    if (key === 'reviews') {
      const widget =
        this.targetScope.querySelector('#judgeme_product_reviews') ||
        this.targetScope.querySelector('[data-product-info-reviews], .jdgm-review-widget, .jdgm-widget');
      return widget;
    }

    const pattern = HEADING_PATTERNS[key];
    if (!pattern) return null;

    return [...this.targetScope.querySelectorAll('details')].find((details) => {
      if (this.contains(details)) return false;
      const summary = details.querySelector(':scope > summary');
      return pattern.test(summary?.textContent?.trim() || '');
    }) || null;
  }

  handleClick = (event) => {
    const link = event.target.closest('[data-product-info-link]');
    if (!(link instanceof HTMLButtonElement) || !this.contains(link)) return;

    const key = link.dataset.productInfoLink;
    const target = key ? this.targets.get(key) : null;
    if (!key || !target) return;

    if (target instanceof HTMLDetailsElement) target.open = true;
    this.setActive(key, true);

    const top = window.scrollY + target.getBoundingClientRect().top - this.getTopOffset();
    window.scrollTo({
      top: Math.max(0, top),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  handleResize = () => {
    this.scheduleUpdate();
  };

  scheduleUpdate = () => {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  };

  getHeaderOffset() {
    const header = document.querySelector('#header-component, header-component');
    if (!(header instanceof HTMLElement)) return 0;

    const stickyMode = header.getAttribute('sticky');
    const stickyState = header.dataset.stickyState;
    const isVisible = stickyMode === 'always' || stickyState === 'active';
    return isVisible ? header.getBoundingClientRect().height : 0;
  }

  getTopOffset() {
    return this.getHeaderOffset() + this.offsetHeight + 8;
  }

  updateRevealState() {
    const shouldShow = window.scrollY >= REVEAL_SCROLL_Y;
    this.classList.toggle('is-visible', shouldShow);
    this.toggleAttribute('inert', !shouldShow);
    this.setAttribute('aria-hidden', String(!shouldShow));
  }

  update() {
    this.updateRevealState();
    if (!this.targets.size) return;

    const headerOffset = this.getHeaderOffset();
    const navHeight = this.offsetHeight;
    const activationLine = headerOffset + navHeight + Math.min(96, window.innerHeight * 0.18);
    let activeKey = '';
    let nearestDistance = Number.POSITIVE_INFINITY;

    const targetRects = [...this.targets.entries()]
      .map(([key, target]) => ({ key, target, rect: target.getBoundingClientRect() }))
      .sort((a, b) => a.rect.top - b.rect.top);

    for (const { key, rect } of targetRects) {
      const distance = Math.abs(rect.top - activationLine);
      if (rect.top <= activationLine && rect.bottom > activationLine) {
        activeKey = key;
        break;
      }
      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeKey = key;
      }
    }

    if (activeKey) this.setActive(activeKey);

    this.style.setProperty('--product-information-navigation-top', `${headerOffset}px`);

  }

  setActive(key, reveal = false) {
    if (key === this.activeKey && !reveal) return;
    this.activeKey = key;

    for (const [linkKey, link] of this.links) {
      if (linkKey === key) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    }

    const activeLink = this.links.get(key);
    if (!activeLink || !this.scroller) return;

    const left = activeLink.offsetLeft - (this.scroller.clientWidth - activeLink.offsetWidth) / 2;
    this.scroller.scrollTo({
      left: Math.max(0, left),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('product-information-navigation')) {
  customElements.define('product-information-navigation', ProductInformationNavigation);
}
